import { BrDivider } from '@govbr-ds/react-components';
import styles from './secaoDeducoes.module.scss';
import { formatCurrency, removeFormatCurrency } from 'utils/formatCurrency';
import Deducao from './components/deducao';
import { ITributoAdicionado, ITributoPossivel, ITributos } from 'interfaces/ITributos';
import CompraTerceiroInputs from 'pages/TitularArrendatario/components/titularArrendatarioProcessoMinerario/titularArrendatarioOutrosRegimes/components/componenteNCM/components/compraTerceiroInputs/compraTerceiroInputs';
import AnmSwitch from 'components/anmSwitch/anmSwitch';

interface ISecaoDeducoesProps {
  deducoesPossiveis: ITributos | undefined;
  deducoesAdicionadas: ITributoAdicionado[];
  setDeducoesAdicionadas: React.Dispatch<React.SetStateAction<ITributoAdicionado[]>>;
  semMargem?: boolean;
  valorReceitaBruta?: string;
  isApresentarSecaoCompraTerceiros?: boolean;
  haCompraTerceiro?: boolean;
  setHaCompraTerceiro?: React.Dispatch<React.SetStateAction<boolean>>;
  valorCompraTerceiro?: string;
  setValorCompraTerceiro?: React.Dispatch<React.SetStateAction<string>>;
  quantidadeCompraTerceiro?: string;
  setQuantidadeCompraTerceiro?: React.Dispatch<React.SetStateAction<string>>;
}

const SecaoDeducoes = ({
  deducoesPossiveis,
  deducoesAdicionadas,
  semMargem = false,
  setDeducoesAdicionadas,
  valorReceitaBruta,
  isApresentarSecaoCompraTerceiros = false,
  haCompraTerceiro = false,
  setHaCompraTerceiro = () => {},
  valorCompraTerceiro = '',
  setValorCompraTerceiro = () => {},
  quantidadeCompraTerceiro = '',
  setQuantidadeCompraTerceiro = () => {},
}: ISecaoDeducoesProps) => {
  const containerClass = semMargem ? styles.containerSemMargem : styles.container;

  const atualizarValorDeducoes = (tipoDeducao: string, novoValor: number | '') => {
    if (novoValor === '') {
      setDeducoesAdicionadas((prev) => prev.filter((da) => da.nome !== tipoDeducao));
      return;
    }

    setDeducoesAdicionadas((prev) => {
      const indiceExistente = prev.findIndex((deducao) => deducao.nome === tipoDeducao);

      if (indiceExistente >= 0) {
        return prev.map((deducao, index) => (index === indiceExistente ? { ...deducao, valor: novoValor } : deducao));
      }

      return [...prev, { nome: tipoDeducao, valor: novoValor }];
    });
  };

  const totalDeducoes =
    deducoesAdicionadas?.reduce((acumulador, deducao) => acumulador + deducao.valor, 0) +
    removeFormatCurrency(valorCompraTerceiro);
  const totalReceitaLiquida =
    valorReceitaBruta === undefined ? valorReceitaBruta : removeFormatCurrency(valorReceitaBruta) - totalDeducoes;

  const compraTerceirosHtml = () => {
    if (!isApresentarSecaoCompraTerceiros) return;
    return (
      <div className={styles.containerCompraTerceiros}>
        <div className={styles.camposContainer}>
          <div className={styles.switchContainer}>
            <AnmSwitch
              switchDataTestId='switch-compra-terceiro'
              label='Deseja abater compras de terceiros?'
              value={haCompraTerceiro}
              setValue={setHaCompraTerceiro}
              tooltipText='Essa marcação só deve ser feita quando o bem mineral adquirido de terceiros for blendado com minério de produção própria e comercializado no CFOP de produção do estabelecimento.'
            />
          </div>
        </div>
        {haCompraTerceiro && (
          <CompraTerceiroInputs
            quantidade={quantidadeCompraTerceiro}
            setQuantidade={setQuantidadeCompraTerceiro}
            valorTotal={valorCompraTerceiro}
            setValorTotal={setValorCompraTerceiro}
          />
        )}
        <BrDivider className={styles.divider}></BrDivider>
      </div>
    );
  };

  const deducoesHtml = (
    deducoesRecebidas: ITributoPossivel[] | undefined | [],
    deducoesAdicionadas: ITributoAdicionado[],
    tituloSecao: string,
    incluiDivider?: boolean
  ) => {
    if (!deducoesRecebidas || deducoesRecebidas.length === 0) return null;

    const deducoesRegulares = deducoesRecebidas.map((deducao) => (
      <Deducao
        key={deducao.id}
        nome={deducao.nome}
        isViewOnly={false}
        onChange={atualizarValorDeducoes}
        deducaoAdicionada={deducoesAdicionadas.find((da) => da.nome === deducao.nome)}
      ></Deducao>
    ));

    return (
      <>
        <div className={styles.containerInterno}>
          <div className={styles.containerTituloInterno}>
            <span className={styles.titulo}>{tituloSecao}</span>
          </div>
          <BrDivider className={styles.divider}></BrDivider>
          {deducoesRegulares}
        </div>
        {incluiDivider && <BrDivider className={styles.divider}></BrDivider>}
      </>
    );
  };

  const apresentarDeducao = (deducoesPossiveis: ITributos | undefined) => {
    return (
      <div className={containerClass}>
        <span className={styles.titulo}>Deduções</span>
        <BrDivider className={styles.divider}></BrDivider>
        <div className={styles.containerTotais}>
          <div className={styles.containerTotal}>
            <label>Total Deduções (R$)</label>
            <span>{formatCurrency(totalDeducoes)}</span>
          </div>
          {totalReceitaLiquida !== undefined && (
            <div className={styles.containerTotal}>
              <label>Receita Líquida (R$)</label>
              <span>{formatCurrency(totalReceitaLiquida)}</span>
            </div>
          )}
        </div>
        <BrDivider className={styles.divider}></BrDivider>
        {compraTerceirosHtml()}
        {deducoesHtml(deducoesPossiveis?.tributosRegulares, deducoesAdicionadas, 'Tributos Incidentes')}
        {deducoesHtml(deducoesPossiveis?.tributosEspeciais, deducoesAdicionadas, 'Outras Deduções')}
      </div>
    );
  };

  return apresentarDeducao(deducoesPossiveis);
};

export default SecaoDeducoes;
